import { Accounts_CountToGenerate, Accounts_GenerateAccesses } from "data/endpoints";
import { useQuery } from "data/hooks/useQuery";
import IRepository from "data/IRepository";
import { GenerateAccessesTaskState } from "features/accounts/enums/generateAccessesTaskState";
import Alert from "features/shared/alerts/components/Alert";
import { AlertType } from "features/shared/alerts/enums/alertType";
import Card from "features/shared/components/card/Card";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import OptionType from "features/shared/components/selectInput/OptionType";
import SelectInput from "features/shared/components/selectInput/SelectInput";
import { formatNumber } from "features/shared/helpers/Formatters";
import { getQueryParams } from "features/shared/helpers/queryParams";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { trackPromise } from "react-promise-tracker";

type Props = {};

const GenerateAccountsSection = (props: Props) => {
  // DI
  const generateAccessesRepository = useInjection<
    IRepository<GenerateAccessesTaskState>
  >(IocTypes.Repository);

  // translations
  const tLabels = useTranslation("admin\\administration").t;

  // state
  const [selectedDelay, setSelectedDelay] = useState(20);
  const [generateAccessesState, setGenerateAccessesState] = useState<GenerateAccessesTaskState>("Undefined");
  const [refreshCount, setRefreshCount] = useState(false);

  // fetch
  const { data: countToGenerate } = useQuery<number>({ url: Accounts_CountToGenerate}, -1, [refreshCount]);

  // helpers
  const getAlertType = (): AlertType => { 
    let alertType: AlertType = "Info";

    if (countToGenerate > 0) {
      switch (generateAccessesState) {
        case "Started":
          alertType = "Success";
          break;
        case "Running":
          alertType = "Warning";
          break;
        default:
          break;
      }
    }

    return alertType;
  }
  
  const getAlertMessage = (): string => { 
    let alertMessage = tLabels("gnerateAccountsSection.noAccounts");

    if (countToGenerate > 0) {
      switch (generateAccessesState) {
        case "Started":
          alertMessage = tLabels("gnerateAccountsSection.generationStarted");
          break;
        case "Running":
          alertMessage = tLabels("gnerateAccountsSection.generationRunning");
          break;
        default:
          break;
      }
    }

    return alertMessage;
  }

  // handlers
  const generateAccessesHandler = async () => {
    if (countToGenerate > 0) {
      let state = await trackPromise(
        generateAccessesRepository.getOne({
          url: Accounts_GenerateAccesses,
          params: getQueryParams("Delay", (selectedDelay * 60).toString()),
        })
      );
  
      setGenerateAccessesState(state);  
    }
  };

  const refreshCountHandler = () => { 
    setRefreshCount(!refreshCount);
  }

  // sub-renders
  const renderDelaySelect = () => {
    let options: OptionType[] = [];

    for (let i = 0; i <= 20; i += 5) {
      options.push({
        value: String(i),
        name: String(`${i} ${tLabels("gnerateAccountsSection.minutes")}`),
      });
    }

    return (
      <SelectInput
        name="delaySelect"
        options={options}
        value={selectedDelay}
        onChange={(e) => setSelectedDelay(Number(e.target.value))}
        className="mb-0 w-100"
      />  
    );
  };

  // render
  return (
    <>
      <Card className="p-3 mb-4">
        <div className="row no-gutters align-items-center mx-n2 mx-sm-0">
          <div className="col-12 col-sm-6 col-md-auto pr-sm-3 px-2 px-sm-0">
            <div className="row no-gutters align-items-center mb-3 mb-md-0">
              <div className="col-12 col-md-auto">
                <span className="mr-2 text-muted">
                  {tLabels("gnerateAccountsSection.toSync")}:
                </span>
              </div>
              <div className="col-12 col-md">
                <input
                  className="form-control d-inline-block"
                  disabled
                  value={countToGenerate == -1 ? "" : formatNumber(countToGenerate)}
                  size={5}
                />
              </div>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-auto px-2 pr-md-3 px-sm-0">
            <div className="d-flex flex-column flex-md-row align-items-md-center mb-3 mb-md-0">
              <span className="mr-2 text-muted">
                {tLabels("gnerateAccountsSection.delay")}:
              </span>
              {renderDelaySelect()}
            </div>
          </div>
          <div className="col-md-auto ml-md-auto px-2 px-sm-0">
            <button 
              className="btn btn-sm btn-secondary mr-2 px-3" 
              onClick={refreshCountHandler}
            >
              {tLabels("gnerateAccountsSection.refresh")}
            </button>
            <button
              className="btn btn-sm btn-primary px-3"
              onClick={generateAccessesHandler}
              disabled={countToGenerate == 0}
            >
              {tLabels("gnerateAccountsSection.start")}
            </button>
          </div>
        </div>
      </Card>
      <RenderWrapper isVisible={() => countToGenerate === 0 || generateAccessesState !== "Undefined"}>
        <Alert
          alertObj={{
            alertType: getAlertType(),
            message: getAlertMessage()
          }}
        />
      </RenderWrapper>
    </>
  );
};

export default GenerateAccountsSection;
